import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { initAnalytics } from "@/lib/analytics";
import { useSectionViews } from "@/hooks/useSectionViews";

import TopStrip from "@/sections/TopStrip";
import Header from "@/sections/Header";
import Hero from "@/sections/Hero";
import LogoMarquee from "@/sections/LogoMarquee";
import Stats from "@/sections/Stats";
import Services from "@/sections/Services";
import About from "@/sections/About";
import CaseStudy from "@/sections/CaseStudy";
import Work from "@/sections/Work";
import Testimonials from "@/sections/Testimonials";
import Footer from "@/sections/Footer";
import BrandFooter from "@/sections/BrandFooter";
import Process from "@/sections/Process";
import StickyCTA from "@/sections/StickyCTA";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsConditions from "@/pages/TermsConditions";
import FAQPage from "@/pages/FAQPage";
import AdminPage from "@/admin/AdminPage";
import AuthCallback from "@/admin/AuthCallback";

// Brand-blue accent bar — used as a rhythm divider between sections
const AccentBar = () => (
  <div
    aria-hidden="true"
    className="h-[6px] w-full bg-gradient-to-r from-[#0A1130] via-[#1440E1] to-[#0A1130] relative"
    data-testid="brand-accent-bar"
  >
    <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-transparent pointer-events-none" />
  </div>
);

// Soft gradient divider — smooths the navy Footer into the blue BrandFooter
const SectionDivider = () => (
  <div
    aria-hidden="true"
    className="relative h-12 bg-gradient-to-b from-[#050B26] via-[#0B1A6B] to-[#1A3DE5]"
    data-testid="footer-divider"
  >
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
  </div>
);

const Landing = () => {
  useSectionViews();
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white antialiased">
      <TopStrip />
      <Header />
      <main>
        <Hero />
        <LogoMarquee />
        <Stats />
        <AccentBar />
        <Services />
        <Process />
        <CaseStudy />
        <Work />
        <Testimonials />
        <About />
      </main>
      <Footer />
      <SectionDivider />
      <BrandFooter />
      <StickyCTA />
    </div>
  );
};

const LegalLayout = ({ children }) => (
  <div className="min-h-screen bg-[#0A0A0A] text-white antialiased">
    <TopStrip />
    <Header />
    <main>{children}</main>
    <Footer />
    <BrandFooter />
  </div>
);

function AppRouter() {
  const location = useLocation();
  // Process the Emergent OAuth return BEFORE normal routing to avoid race conditions.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route
        path="/privacy-policy"
        element={
          <LegalLayout>
            <PrivacyPolicy />
          </LegalLayout>
        }
      />
      <Route
        path="/terms-and-conditions"
        element={
          <LegalLayout>
            <TermsConditions />
          </LegalLayout>
        }
      />
      <Route
        path="/faq"
        element={
          <LegalLayout>
            <FAQPage />
          </LegalLayout>
        }
      />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    initAnalytics();
    // Protect images/videos: block right-click "Save image" and drag-to-save.
    const isMedia = (el) =>
      el && (el.tagName === "IMG" || el.tagName === "VIDEO" || el.tagName === "PICTURE");
    const blockContextMenu = (e) => {
      if (isMedia(e.target)) e.preventDefault();
    };
    const blockDrag = (e) => {
      if (isMedia(e.target)) e.preventDefault();
    };
    document.addEventListener("contextmenu", blockContextMenu);
    document.addEventListener("dragstart", blockDrag);
    return () => {
      document.removeEventListener("contextmenu", blockContextMenu);
      document.removeEventListener("dragstart", blockDrag);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run-once on mount; initAnalytics is a stable module import
  }, []);

  return (
    <div className="App overflow-x-hidden">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
